import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type { Territory } from '../../lib/types';
import { Field, SettingsCard } from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import { Drawer } from '@allrouted/ui/Drawer';
import { IconButton, RefreshIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';

/**
 * What has been asked of this API, and what it answered.
 *
 * The conversation this exists for is specific: an integration says "we sent
 * you this and nothing happened", and the useful reply is the bytes they
 * actually sent. So the list is scannable at a glance -- method, path, status,
 * who -- and one click gives the whole exchange.
 *
 * Two panes rather than a table with expanding rows: a request body can be a
 * thousand orders long, and a row that grows to a page pushes everything you
 * were comparing it against off the screen.
 */

interface CallRow extends Record<string, unknown> {
  id: number;
  at: string;
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  surface: string;
  caller_kind: string;
  error_code: string | null;
  api_key_label: string | null;
  user_email: string | null;
  territory_name: string | null;
  request_bytes: number;
  response_bytes: number;
}

interface DeliveryRow extends Record<string, unknown> {
  id: string;
  event: string;
  url: string;
  status: string;
  attempts: number;
  last_status: number | null;
  last_error: string | null;
  created_at: string;
  delivered_at: string | null;
  replays: number;
  last_replay_at: string | null;
  last_replay_by_email: string | null;
  territory_name: string | null;
}

const bytes = (n: number): string =>
  n === 0 ? '—' : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`;

const when = (iso: string): string => new Date(iso).toLocaleString();

/** Pretty-print JSON where it is JSON, and leave it alone where it is not. */
function pretty(text: string | null): string {
  if (!text) return '';
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function statusTone(status: number): 'ok' | 'warn' | 'late' {
  if (status >= 500) return 'late';
  if (status >= 400) return 'warn';
  return 'ok';
}

export function AuditView({ territories }: { territories: Territory[] }) {
  const [tab, setTab] = useState<'calls' | 'webhooks'>('calls');

  return (
    <div className="view">
      {/* `aria-selected` rather than a data attribute, as the other tabs here do. */}
      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className="tab"
          aria-selected={tab === 'calls'}
          onClick={() => setTab('calls')}
        >
          API calls
        </button>
        <button
          type="button"
          role="tab"
          className="tab"
          aria-selected={tab === 'webhooks'}
          onClick={() => setTab('webhooks')}
        >
          Webhook deliveries
        </button>
      </div>

      {tab === 'calls' ? <CallsPanel territories={territories} /> : <WebhooksPanel territories={territories} />}
    </div>
  );
}

function CallsPanel({ territories }: { territories: Territory[] }) {
  const ui = useUi();
  const [rows, setRows] = useState<CallRow[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Record<string, unknown> | null>(null);

  const [territoryId, setTerritoryId] = useState('');
  const [surface, setSurface] = useState('');
  const [method, setMethod] = useState('');
  const [outcome, setOutcome] = useState('all');
  const [path, setPath] = useState('');

  const load = useCallback(
    async (before?: number) => {
      setLoading(true);
      try {
        /*
         * Flush first. Records are written in batches, so a call made a moment
         * ago may not be stored yet -- and "I just did that and it is not
         * here" reads as a broken audit trail rather than a fast one.
         */
        if (!before) await api.flushAudit().catch(() => {});
        const result = await api.auditCalls({
          ...(territoryId ? { territoryId } : {}),
          ...(surface ? { surface } : {}),
          ...(method ? { method } : {}),
          ...(outcome !== 'all' ? { outcome } : {}),
          ...(path ? { path } : {}),
          ...(before ? { before: String(before) } : {}),
        });
        setRows((current) => (before ? [...current, ...(result.calls as CallRow[])] : (result.calls as CallRow[])));
        setNextBefore(result.nextBefore);
      } catch (err) {
        ui.notify('error', 'Could not load calls', err instanceof ApiError ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [territoryId, surface, method, outcome, path],
  );

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryId, surface, method, outcome]);

  const openCall = async (id: number) => {
    try {
      setOpen(await api.auditCall(String(id)));
    } catch (err) {
      ui.notify('error', 'Could not load that call', err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <>
      <SettingsCard
        title="API calls"
        description="Every request this account has made, with the full request and response."
        actions={
          <IconButton label="Refresh" onClick={() => void load()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        }
      >
        <div className="audit-filters">
          <Field label="Territory">
            <Select
              value={territoryId}
              options={[
                { value: '', label: 'All territories' },
                ...territories.map((t) => ({ value: t.id, label: t.name })),
              ]}
              onChange={setTerritoryId}
            />
          </Field>
          <Field label="Surface">
            <Select
              value={surface}
              options={[
                { value: '', label: 'Any' },
                { value: 'v1', label: 'Modern /v1' },
                { value: 'compat', label: 'Compat /api/v1' },
                { value: 'public', label: 'Public' },
                { value: 'internal', label: 'Internal' },
              ]}
              onChange={setSurface}
            />
          </Field>
          <Field label="Method">
            <Select
              value={method}
              options={['', 'GET', 'POST', 'PUT', 'DELETE'].map((m) => ({
                value: m,
                label: m || 'Any',
              }))}
              onChange={setMethod}
            />
          </Field>
          <Field label="Outcome">
            <Select
              value={outcome}
              options={[
                { value: 'all', label: 'All' },
                { value: 'errors', label: 'Errors only' },
              ]}
              onChange={setOutcome}
            />
          </Field>
          <Field label="Path contains">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void load();
              }}
            >
              <input
                className="text-input"
                value={path}
                placeholder="/orders"
                onChange={(e) => setPath(e.target.value)}
              />
            </form>
          </Field>
        </div>

        <div className="table-scroll">
          <table className="grid audit-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Method</th>
                <th>Path</th>
                <th className="num">Status</th>
                <th className="num">Took</th>
                <th>Caller</th>
                <th className="num">In / out</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => void openCall(r.id)} className="audit-row">
                  <td className="mono">{when(r.at)}</td>
                  <td className="mono">{r.method}</td>
                  <td className="audit-path" title={r.path}>
                    {r.path}
                  </td>
                  <td className="num">
                    <span className="delta" data-tone={statusTone(r.status)}>
                      {r.status}
                    </span>
                  </td>
                  <td className="num mono">{r.duration_ms} ms</td>
                  <td>
                    {r.api_key_label ?? r.user_email ?? r.caller_kind}
                    <small className="audit-sub"> {r.surface}</small>
                  </td>
                  <td className="num mono">
                    {bytes(r.request_bytes)} / {bytes(r.response_bytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && !loading ? (
          <div className="empty">Nothing matches those filters.</div>
        ) : null}

        {nextBefore ? (
          <div className="row-actions">
            <button type="button" className="btn" disabled={loading} onClick={() => void load(nextBefore)}>
              {loading ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </SettingsCard>

      <CallDrawer call={open} onClose={() => setOpen(null)} />
    </>
  );
}

function CallDrawer({
  call,
  onClose,
}: {
  call: Record<string, unknown> | null;
  onClose: () => void;
}) {
  if (!call) return null;
  const headers = (call.request_headers ?? {}) as Record<string, string>;

  return (
    <Drawer
      open={!!call}
      onClose={onClose}
      title={`${String(call.method)} ${String(call.path)}`}
      subtitle={`${String(call.status)} · ${String(call.duration_ms)} ms · ${when(String(call.at))}`}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="stat-row">
        <Stat label="Surface" value={String(call.surface)} />
        <Stat label="Caller" value={String(call.api_key_label ?? call.user_email ?? call.caller_kind)} />
        <Stat label="Territory" value={String(call.territory_name ?? '—')} />
        <Stat label="Address" value={String(call.ip ?? '—')} />
      </div>

      {call.truncated ? (
        <div className="banner" data-tone="warn">
          <div>
            <b>A body was too large to keep whole</b>
            <p>
              What is shown is the first part of it. Raise <code>AUDIT_MAX_BODY_BYTES</code> if you
              need the rest kept.
            </p>
          </div>
        </div>
      ) : null}

      <h3 className="section-label">Request</h3>
      {call.query ? (
        <Field label="Query">
          <pre className="audit-body">{pretty(JSON.stringify(call.query))}</pre>
        </Field>
      ) : null}
      <Field label="Headers" wide>
        <pre className="audit-body">
          {Object.entries(headers)
            .map(([k, v]) => `${k}: ${v}`)
            .join('\n')}
        </pre>
      </Field>
      <Field label="Body" wide>
        <pre className="audit-body">{pretty(call.request_body as string | null) || '(empty)'}</pre>
      </Field>

      <h3 className="section-label">Response</h3>
      <Field label="Body" wide>
        <pre className="audit-body">{pretty(call.response_body as string | null) || '(empty)'}</pre>
      </Field>

      <p className="disclosure-note">
        Anything that looked like a credential — an API key, a password, a signing secret — was
        replaced before this was stored, so it is not here to leak.
      </p>
    </Drawer>
  );
}

function WebhooksPanel({ territories }: { territories: Territory[] }) {
  const ui = useUi();
  const [rows, setRows] = useState<DeliveryRow[]>([]);
  const [territoryId, setTerritoryId] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { deliveries } = await api.auditWebhooks({
        ...(territoryId ? { territoryId } : {}),
        ...(status ? { status } : {}),
      });
      setRows(deliveries as DeliveryRow[]);
    } catch (err) {
      ui.notify('error', 'Could not load deliveries', err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const replay = async (id: string, url?: string) => {
    try {
      const result = await api.replayWebhook(id, url ? { url } : {});
      ui.notify(
        'ok',
        'Queued for delivery',
        `Attempt ${Number(result.replays)} — the worker picks it up within a minute.`,
      );
      setOpen(null);
      await load();
    } catch (err) {
      ui.notify('error', 'Could not replay', err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <>
      <SettingsCard
        title="Webhook deliveries"
        description="Outbound callbacks, what came back, and a way to send one again."
        actions={
          <IconButton label="Refresh" onClick={() => void load()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        }
      >
        <div className="audit-filters">
          <Field label="Territory">
            <Select
              value={territoryId}
              options={[
                { value: '', label: 'All territories' },
                ...territories.map((t) => ({ value: t.id, label: t.name })),
              ]}
              onChange={setTerritoryId}
            />
          </Field>
          <Field label="Status">
            <Select
              value={status}
              options={[
                { value: '', label: 'Any' },
                { value: 'pending', label: 'Pending' },
                { value: 'delivered', label: 'Delivered' },
                { value: 'failed', label: 'Failed' },
              ]}
              onChange={setStatus}
            />
          </Field>
        </div>

        <div className="table-scroll">
          <table className="grid audit-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>URL</th>
                <th>Status</th>
                <th className="num">Attempts</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="audit-row" onClick={() => void openDelivery(d.id, setOpen, ui)}>
                  <td className="mono">{when(d.created_at)}</td>
                  <td>{d.event}</td>
                  <td className="audit-path" title={d.url}>
                    {d.url}
                  </td>
                  <td>
                    <span
                      className="delta"
                      data-tone={
                        d.status === 'delivered' ? 'ok' : d.status === 'failed' ? 'late' : 'idle'
                      }
                    >
                      {d.status}
                    </span>
                    {d.last_status ? <small className="audit-sub"> {d.last_status}</small> : null}
                  </td>
                  <td className="num mono">
                    {d.attempts}
                    {d.replays > 0 ? <small className="audit-sub"> +{d.replays} replayed</small> : null}
                  </td>
                  <td className="num">
                    <button
                      type="button"
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        void replay(d.id);
                      }}
                    >
                      Replay
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && !loading ? (
          <div className="empty">No deliveries yet.</div>
        ) : null}
      </SettingsCard>

      <DeliveryDrawer
        delivery={open}
        onClose={() => setOpen(null)}
        onReplay={(url) => void replay(String(open?.id), url)}
      />
    </>
  );
}

async function openDelivery(
  id: string,
  set: (v: Record<string, unknown> | null) => void,
  ui: ReturnType<typeof useUi>,
) {
  try {
    set(await api.auditWebhook(id));
  } catch (err) {
    ui.notify('error', 'Could not load', err instanceof ApiError ? err.message : String(err));
  }
}

function DeliveryDrawer({
  delivery,
  onClose,
  onReplay,
}: {
  delivery: Record<string, unknown> | null;
  onClose: () => void;
  onReplay: (url?: string) => void;
}) {
  const [url, setUrl] = useState('');
  if (!delivery) return null;

  return (
    <Drawer
      open={!!delivery}
      onClose={onClose}
      title={String(delivery.event)}
      subtitle={`${String(delivery.status)} · ${String(delivery.attempts)} attempts · ${when(String(delivery.created_at))}`}
      footer={
        <>
          <button type="button" className="btn btn-primary" onClick={() => onReplay(url || undefined)}>
            {url ? 'Replay to that URL' : 'Replay'}
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="stat-row">
        <Stat label="Attempts" value={String(delivery.attempts)} />
        <Stat label="Replays" value={String(delivery.replays ?? 0)} />
        <Stat label="Last response" value={String(delivery.last_status ?? '—')} />
        <Stat label="Territory" value={String(delivery.territory_name ?? '—')} />
      </div>

      <Field label="URL" wide>
        <pre className="audit-body">{String(delivery.url)}</pre>
      </Field>

      {delivery.last_error ? (
        <Field label="Last error" wide>
          <pre className="audit-body">{String(delivery.last_error)}</pre>
        </Field>
      ) : null}

      <h3 className="section-label">Payload</h3>
      <Field label="Body" wide>
        <pre className="audit-body">{pretty(JSON.stringify(delivery.payload))}</pre>
      </Field>

      <h3 className="section-label">Replay</h3>
      <p className="disclosure-note">
        Sending it again resets this delivery to pending and keeps its id, so the history stays one
        row. Give a different URL to send the same payload somewhere else — useful for pointing a
        failed delivery at a request-capture endpoint to see exactly what the receiver would get.
      </p>
      <Field label="Send to a different URL" hint="Leave blank to use the original">
        <input
          className="text-input"
          value={url}
          placeholder={String(delivery.url)}
          onChange={(e) => setUrl(e.target.value)}
        />
      </Field>
    </Drawer>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}
